import { useEffect, useRef, useState } from 'preact/hooks';
import { useLocation, useRoute } from 'preact-iso';
import { getQuote, saveQuote, calculate, deleteQuote, duplicateQuote, getCustomer, addOption, listOptions, removeOption } from '../lib/admin.js';
import { emptyQuote, localTotals, lineTotal, money, quoteState, quoteNumber, round2, uid, daysAgo, SHIP_KINDS, standardPrice, withShipping } from '../lib/quote.js';
import { attrsOf, displayDetail, displayTitle, setAttr, systemKind, SYSTEM_LABEL, variantFor, addonPlan, addonLine, paintOunces } from '../lib/paint.js';
import { adminUrl, draftGid, idOf, useApp } from '../lib/app-state.js';
import { busy, toast } from '../lib/server.js';
import PaintAdder, { Swatch } from './PaintAdder.jsx';
import { CustomerPicker, CustomAdder, ProductAdder } from './Pickers.jsx';

const SAVE_BAR = 'quote-save-bar';

function LineRow({ l, onChange, onRemove, onEdit, editing, locked }) {
  const a = attrsOf(l);
  const detail = displayDetail(l);
  const custom = !!a['Custom fill'] || a._unit === 'oz';
  return (
    <s-table-row>
      <s-table-cell>
        <s-stack direction="inline" gap="base" alignItems="center">
          {l.kind === 'paint' ? <Swatch hex={l.hex} fx={a._fx} name={a.Color} /> : l.image ? <img class="thumb" src={l.image} alt="" /> : <span class="thumb" />}
          <s-stack gap="none">
            {locked ? (
              <s-text type="strong">{displayTitle(l)}</s-text>
            ) : (
              <s-clickable onClick={onEdit} accessibilityLabel={`Edit ${displayTitle(l)}`}>
                <s-text type="strong">{displayTitle(l)}</s-text>
              </s-clickable>
            )}
            {detail && <s-text color="subdued">{detail}</s-text>}
          </s-stack>
        </s-stack>
      </s-table-cell>
      <s-table-cell>
        <div class="qty-field">
          <s-number-field
            label={custom ? 'Ounces' : 'Quantity'}
            labelAccessibilityVisibility="exclusive"
            min={1}
            step={1}
            value={String(l.quantity)}
            suffix={custom ? 'oz' : undefined}
            disabled={locked}
            onChange={(e) => {
              const quantity = Math.max(1, parseInt(e.currentTarget.value, 10) || 1);
              onChange(a._unit === 'oz' ? { quantity, attributes: setAttr(l.attributes, 'Amount', `${quantity} oz`) } : { quantity });
            }}
          />
        </div>
      </s-table-cell>
      <s-table-cell>
        <div class="price-field">
          <s-money-field
            label="Unit price"
            labelAccessibilityVisibility="exclusive"
            value={Number(l.unitPrice).toFixed(2)}
            disabled={locked}
            details={l.variantId && round2(l.unitPrice) !== round2(l.listPrice) ? `List ${money(l.listPrice)}` : undefined}
            onChange={(e) => {
              const v = parseFloat(e.currentTarget.value);
              onChange({ unitPrice: v >= 0 ? round2(v) : l.listPrice });
            }}
          />
        </div>
      </s-table-cell>
      <s-table-cell>
        <span class="num">{money(lineTotal(l))}</span>
      </s-table-cell>
      <s-table-cell>
        {!locked && (
          <s-stack direction="inline" gap="none">
            <s-button variant="tertiary" icon="edit" accessibilityLabel={`Edit ${displayTitle(l)}`} pressed={editing} onClick={onEdit} />
            <s-button variant="tertiary" icon="delete" accessibilityLabel={`Remove ${displayTitle(l)}`} onClick={onRemove} />
          </s-stack>
        )}
      </s-table-cell>
    </s-table-row>
  );
}

/* Edit a line in place: what it is called on the quote, the colour details the counter reads, system and size
   (another variant of the same formula tier), and a note the customer sees under it. */
function LineEditor({ l, onChange, onClose, onAddLines, lines }) {
  const { paint, addons, settings } = useApp();
  const a = attrsOf(l);
  const set = (key) => (e) => onChange({ attributes: setAttr(l.attributes, key, e.currentTarget.value) });
  const v = paint?.variants.find((x) => x.id === l.variantId);
  const [system, size] = (l.variantTitle || '').split(' / ');
  const swap = (sys, sz) => {
    const n = variantFor(paint, sys, sz, v?.tier || 'Standard');
    if (!n || n.id === l.variantId) return;
    onChange({ variantId: n.id, variantTitle: n.title, sku: n.sku, listPrice: n.price, unitPrice: n.price });
  };
  const autoTitle = displayTitle({ ...l, attributes: setAttr(l.attributes, '_quote_title', '') });
  /* single stage needs its activator: offer the poured ounces for this line when the quote has none for it */
  const forPaint = `${a.Color || ''}${a['Paint code'] ? ` (${a['Paint code']})` : ''}`;
  const hasAct = lines.some((x) => attrsOf(x).Role === 'Activator' && attrsOf(x)['For paint'] === forPaint);
  const act = l.kind === 'paint' && paint && !hasAct
    ? addonPlan({ lines: [l], tiers: { tri: !!a.Component }, system }, paint, addons || {}, { activator: settings.activatorRatio, activatorProduct: settings.activatorProduct }).find((x) => x.group === 'activator')
    : null;
  return (
    <s-box padding="base" background="subdued" borderRadius="base" border="base">
      <s-stack gap="base">
        <s-stack direction="inline" justifyContent="space-between" alignItems="center">
          <s-heading>Edit line</s-heading>
          <s-button variant="tertiary" onClick={onClose}>Done</s-button>
        </s-stack>
        {l.kind === 'custom' ? (
          <s-text-field label="Item" value={l.title} onInput={(e) => onChange({ title: e.currentTarget.value })} />
        ) : (
          <s-text-field label="Name on the quote" placeholder={autoTitle} value={a._quote_title || ''} onInput={set('_quote_title')}
            details="Leave empty for the automatic name. The order keeps the product's own title." />
        )}
        {l.kind === 'paint' && (
          <s-stack gap="base">
            <s-grid gridTemplateColumns="1fr 1fr" gap="base">
              <s-text-field label="Color name" value={a.Color || ''} onInput={set('Color')} />
              <s-text-field label="Paint code" required value={a['Paint code'] || ''} onInput={set('Paint code')}
                error={a['Paint code'] ? undefined : 'The counter mixes from this code'} />
            </s-grid>
            {paint && v && (
              <s-grid gridTemplateColumns="1fr 1fr" gap="base">
                <s-select label="System" value={system} disabled={!!a.Component} onChange={(e) => swap(e.currentTarget.value, v.kind === 'custom' ? v.size : size)}
                  details={a.Component ? 'A tricoat is basecoat only' : systemKind(system) === 'single' ? 'Single stage — remember the activator' : 'Basecoat — needs clear'}>
                  {paint.systems.map((x) => <s-option key={x} value={x}>{x}{systemKind(x) ? ` (${SYSTEM_LABEL[systemKind(x)].toLowerCase()})` : ''}</s-option>)}
                </s-select>
                <s-select label="Size" value={size} disabled={v.kind === 'custom'} onChange={(e) => swap(system, e.currentTarget.value)}
                  details={v.kind === 'custom' ? 'A custom fill — change the ounces in Qty' : 'Changing size resets the price to the list price'}>
                  {paint.sizes.map((x) => <s-option key={x} value={x}>{x}</s-option>)}
                  {v.kind === 'custom' && <s-option value={size}>{size}</s-option>}
                </s-select>
              </s-grid>
            )}
            <s-text-field label="Vehicle" placeholder="e.g. 2019 Toyota Camry" value={a.Vehicle || ''} onInput={set('Vehicle')} />
            {act && (
              <s-stack direction="inline" gap="base" alignItems="center">
                <s-button icon="plus" onClick={() => onAddLines([addonLine(act, { name: a.Color, code: a['Paint code'] })])}>
                  Add activator — {act.qty} oz ({act.ratio}:1)
                </s-button>
                <s-text color="subdued">{act.title}, poured, {money(act.price)}/oz</s-text>
              </s-stack>
            )}
          </s-stack>
        )}
        <s-text-field label="Note for the customer" placeholder="e.g. Matched to your sample, mixed Tuesday" value={a.Note || ''} onInput={set('Note')}
          details="Shows under this line on the quote, the checkout and the order." />
      </s-stack>
    </s-box>
  );
}

export default function QuoteEditor() {
  const app = useApp();
  const { params } = useRoute();
  const { route, url } = useLocation();
  const isNew = !params.id || params.id === 'new';
  const [q, setQ] = useState(isNew ? emptyQuote(app.settings) : null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [adder, setAdder] = useState(null);
  const [editing, setEditing] = useState(null); // uid of the line being edited
  const [options, setOptions] = useState([]); // the other options of this quote (same qg- tag)
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null); // the option waiting for "Remove" to be confirmed
  const [removeBusy, setRemoveBusy] = useState(false);
  const [calc, setCalc] = useState(null);
  const [calcErr, setCalcErr] = useState('');
  const [changingCustomer, setChangingCustomer] = useState(false);
  const saved = useRef(null);
  const calcTimer = useRef(0);
  const locked = q?.status === 'COMPLETED';

  /* load (or start fresh, optionally for a customer: /quotes/new?customer=123) */
  useEffect(() => {
    if (isNew) {
      const fresh = emptyQuote(app.settings);
      saved.current = fresh;
      setQ(fresh);
      setDirty(false);
      setCalc(null);
      const cid = new URL(url, location.origin).searchParams.get('customer');
      if (cid) getCustomer(`gid://shopify/Customer/${cid}`).then((c) => c && setQ((x) => ({ ...x, customer: c, email: c.email }))).catch(() => {});
      return;
    }
    let live = true;
    setQ(null);
    getQuote(draftGid(params.id))
      .then((r) => {
        if (!live) return;
        /* a saved Standard price that still follows the store rule keeps following it */
        if (r.shipping?.kind === 'std' && r.shipping.price === standardPrice(r, app.settings)) r = { ...r, shipping: { ...r.shipping, manual: false } };
        saved.current = r; setQ(r); setDirty(false); setCalc(r.totals);
      })
      .catch((e) => toast(e.message, true));
    return () => { live = false; };
  }, [params.id]);

  useEffect(() => {
    if (!q?.group) { setOptions([]); return; }
    listOptions(q.group).then(setOptions).catch(() => setOptions([]));
  }, [q?.id, q?.group, q?.totals?.total]);

  /* the save bar is Shopify's own: it appears when something changed and guards leaving */
  useEffect(() => {
    try { dirty ? shopify.saveBar.show(SAVE_BAR) : shopify.saveBar.hide(SAVE_BAR); } catch { /* outside admin */ }
  }, [dirty]);
  useEffect(() => () => { try { shopify.saveBar.hide(SAVE_BAR); } catch { /* */ } }, []);

  /* Shopify's totals (tax by the customer's address) a moment after each change */
  useEffect(() => {
    if (!q || !dirty) return;
    clearTimeout(calcTimer.current);
    if (!q.lines.length) { setCalc(null); return; }
    calcTimer.current = setTimeout(() => {
      calculate(q).then((c) => { setCalc({ ...c, for: q }); setCalcErr(''); }).catch((e) => setCalcErr(e.message));
    }, 600);
    return () => clearTimeout(calcTimer.current);
  }, [q, dirty]);

  if (!q) {
    return (
      <s-page heading="Quote">
        <s-section>
          <s-stack alignItems="center" padding="large"><s-spinner accessibilityLabel="Loading quote" size="large" /></s-stack>
        </s-section>
      </s-page>
    );
  }

  const edit = (patch) => { setQ((x) => withShipping({ ...x, ...(typeof patch === 'function' ? patch(x) : patch) }, app.settings)); setDirty(true); };
  const addLines = (lines) => edit((x) => ({ lines: [...x.lines, ...lines.map((l) => ({ uid: uid(), ...l }))] }));
  /* A poured activator follows its paint: when the paint's size or quantity changes, an activator line still at the
     amount the mix called for is resized to the new amount. One typed by hand is left alone. */
  const changeLine = (u, patch) =>
    edit((x) => {
      const before = x.lines.find((l) => l.uid === u);
      let lines = x.lines.map((l) => (l.uid === u ? { ...l, ...patch } : l));
      if (before?.kind === 'paint' && app.paint && ('quantity' in patch || 'variantId' in patch)) {
        const pa = attrsOf(before);
        const name = `${pa.Color || ''}${pa['Paint code'] ? ` (${pa['Paint code']})` : ''}`;
        const ratio = Number(app.settings.activatorRatio) || 4;
        const same = (ls) => ls.filter((l) => l.kind === 'paint' && `${attrsOf(l).Color || ''}${attrsOf(l)['Paint code'] ? ` (${attrsOf(l)['Paint code']})` : ''}` === name);
        const was = Math.max(1, Math.ceil(paintOunces(same(x.lines), app.paint) / ratio - 1e-9));
        const now = Math.max(1, Math.ceil(paintOunces(same(lines), app.paint) / ratio - 1e-9));
        if (was !== now) {
          lines = lines.map((l) => {
            const a = attrsOf(l);
            return a.Role === 'Activator' && a._unit === 'oz' && a['For paint'] === name && l.quantity === was
              ? { ...l, quantity: now, attributes: setAttr(l.attributes, 'Amount', `${now} oz`) }
              : l;
          });
        }
      }
      return { lines };
    });
  const removeLine = (u) => edit((x) => ({ lines: x.lines.filter((l) => l.uid !== u) }));

  const local = localTotals(q);
  /* Shopify's figures when they are for exactly what is on screen; otherwise ours plus the last tax Shopify gave */
  const fresh = calc && calc.for === q;
  const incl = !!app.shop.taxesIncluded;
  const totals = !dirty
    ? q.totals || { ...local, taxesIncluded: incl }
    : fresh
      ? calc
      : { ...local, taxesIncluded: incl, tax: calc?.tax ?? null, total: incl ? local.total : round2(local.total + (calc?.tax || 0)) };

  const save = async () => {
    if (!q.lines.length) { toast('Add at least one item', true); return null; }
    setSaving(true);
    busy(true);
    try {
      const r = await saveQuote(q);
      saved.current = r;
      setQ(r);
      setCalc(r.totals);
      setDirty(false);
      toast(isNew ? `Quote ${quoteNumber(r)} created` : 'Quote saved');
      if (isNew) route(`/quotes/${idOf(r.id)}`, true);
      return r;
    } catch (e) {
      toast(e.message, true);
      return null;
    } finally {
      setSaving(false);
      busy(false);
    }
  };

  const discard = () => {
    if (isNew) { route('/'); return; }
    setQ(saved.current);
    setCalc(saved.current?.totals || null);
    setDirty(false);
  };

  const saveAndSend = async () => {
    if (!q.customer && !q.email) { toast('Pick the customer first', true); return; }
    const r = dirty || !q.id ? await save() : q;
    if (r?.id) route(`/quotes/${idOf(r.id)}/send`);
  };

  const remove = async () => {
    try { document.getElementById('delete-modal')?.hideOverlay?.(); } catch { /* */ }
    try {
      await deleteQuote(q.id);
      setDirty(false);
      toast('Quote deleted');
      route('/');
    } catch (e) { toast(e.message, true); }
  };

  const duplicate = async () => {
    try {
      const id = await duplicateQuote(q.id);
      toast('Copied into a new quote');
      route(`/quotes/${idOf(id)}`);
    } catch (e) { toast(e.message, true); }
  };

  const newOption = async () => {
    setAdding(true);
    busy(true);
    try {
      const base = dirty ? await save() : q;
      if (!base?.id) return;
      const id = await addOption(base);
      toast('New option created — change what differs (size, kit…)');
      route(`/quotes/${idOf(id)}`);
    } catch (e) {
      toast(e.message, true);
    } finally {
      setAdding(false);
      busy(false);
    }
  };

  const confirmRemove = async () => {
    const o = removing;
    setRemoveBusy(true);
    busy(true);
    try {
      const left = await removeOption(o.id, q.group);
      toast(`${o.option || o.name} removed`);
      setRemoving(null);
      if (o.id === q.id) {
        setDirty(false);
        route(left.length ? `/quotes/${idOf(left[0].id)}` : '/');
      } else if (left.length === 1) {
        const r = await getQuote(q.id);
        saved.current = r;
        setQ(r);
        setDirty(false);
      } else {
        setOptions(left);
      }
    } catch (e) {
      toast(e.message, true);
    } finally {
      setRemoveBusy(false);
      busy(false);
    }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(q.invoiceUrl); toast('Pay link copied'); } catch { toast('Copy the link from the Status box', true); }
  };

  const st = quoteState(q);
  const discount = q.discount || { type: 'FIXED_AMOUNT', value: 0, title: '' };
  const sk = q.shipping ? q.shipping.kind || 'custom' : 'none';
  const ruleNote = `Store rule: ${money(app.settings.shipStandard)}, free from ${money(app.settings.shipFreeOver)}`;

  return (
    <s-page heading={isNew ? 'New quote' : `Quote ${quoteNumber(q)}`} inlineSize="base">
      <ui-save-bar id={SAVE_BAR}>
        <button variant="primary" onClick={save} loading={saving ? '' : undefined}>Save</button>
        <button onClick={discard}>Discard</button>
      </ui-save-bar>

      <s-link slot="breadcrumb-actions" href="/">Quotes</s-link>
      {!locked && (
        <s-button slot="primary-action" variant="primary" disabled={!q.lines.length} onClick={saveAndSend}>
          {q.sentAt ? 'Email again' : 'Email quote'}
        </s-button>
      )}
      {q.id && q.invoiceUrl && !locked && <s-button slot="secondary-actions" onClick={copyLink}>Copy pay link</s-button>}
      {q.id && !locked && <s-button slot="secondary-actions" loading={adding} onClick={newOption}>Add option</s-button>}
      {q.id && <s-button slot="secondary-actions" onClick={duplicate}>Duplicate</s-button>}
      {q.id && <s-button slot="secondary-actions" href={adminUrl(q.id, 'draft_orders')} target="_top">Open in Shopify</s-button>}
      {q.id && !locked && <s-button slot="secondary-actions" tone="critical" commandFor="delete-modal" command="--show">Delete</s-button>}
      {q.id && !locked && (
        <s-modal id="delete-modal" heading={`Delete quote ${quoteNumber(q)}?`}>
          <s-paragraph>The quote and its pay link stop working. This cannot be undone.</s-paragraph>
          <s-button slot="secondary-actions" commandFor="delete-modal" command="--hide">Cancel</s-button>
          <s-button slot="primary-action" variant="primary" tone="critical" onClick={remove}>Delete quote</s-button>
        </s-modal>
      )}

      {locked && (
        <s-banner tone="success" heading={`Paid${q.order ? ` — order ${q.order.name}` : ''}`}>
          <s-paragraph>The customer paid this quote, so it is now an order and can no longer change.</s-paragraph>
          {q.order && <s-button slot="secondary-actions" href={adminUrl(q.order.id, 'orders')} target="_top">View order</s-button>}
        </s-banner>
      )}

      {/* ── customer ── */}
      <s-section heading="Customer">
        {q.customer && !changingCustomer ? (
          <s-stack direction="inline" justifyContent="space-between" alignItems="center">
            <s-stack direction="inline" gap="base" alignItems="center">
              <s-avatar initials={q.customer.displayName.slice(0, 2)} alt={q.customer.displayName} />
              <s-stack gap="none">
                <s-text type="strong">{q.customer.displayName}{q.customer.company ? ` · ${q.customer.company}` : ''}</s-text>
                <s-text color="subdued">{[q.email || q.customer.email || 'No email', q.customer.phone, q.customer.place].filter(Boolean).join(' · ')}</s-text>
              </s-stack>
            </s-stack>
            <s-stack direction="inline" gap="small">
              {(q.email || q.customer.email) && (
                <s-button variant="tertiary" href={`/email?email=${encodeURIComponent(q.email || q.customer.email)}`}>Emails</s-button>
              )}
              {!locked && <s-button variant="tertiary" onClick={() => setChangingCustomer(true)}>Change</s-button>}
            </s-stack>
          </s-stack>
        ) : (
          <CustomerPicker
            autoFocus={isNew}
            onPick={(c) => { edit({ customer: c, email: c.email }); setChangingCustomer(false); }}
          />
        )}
        {q.customer && !q.customer.email && !changingCustomer && (
          <s-box paddingBlockStart="base">
            <s-email-field label="Email for this quote" value={q.email} onInput={(e) => edit({ email: e.currentTarget.value })} />
          </s-box>
        )}
      </s-section>

      {/* ── items ── */}
      <s-section heading="Items" padding={q.lines.length ? 'none' : undefined}>
        {q.lines.length > 0 && (
          <s-table>
            <s-table-header-row>
              <s-table-header listSlot="primary">Item</s-table-header>
              <s-table-header>Qty</s-table-header>
              <s-table-header>Price</s-table-header>
              <s-table-header format="currency">Total</s-table-header>
              <s-table-header />
            </s-table-header-row>
            <s-table-body>
              {q.lines.map((l) => (
                <LineRow key={l.uid} l={l} locked={locked} editing={editing === l.uid} onEdit={() => { setAdder(null); setEditing(editing === l.uid ? null : l.uid); }}
                  onChange={(p) => changeLine(l.uid, p)} onRemove={() => { if (editing === l.uid) setEditing(null); removeLine(l.uid); }} />
              ))}
            </s-table-body>
          </s-table>
        )}
        {!locked && (
          <s-box padding={q.lines.length ? 'base' : 'none'}>
            <s-stack gap="base">
              {editing && q.lines.find((l) => l.uid === editing) && (
                <LineEditor l={q.lines.find((l) => l.uid === editing)} lines={q.lines} onChange={(p) => changeLine(editing, p)} onClose={() => setEditing(null)}
                  onAddLines={(ls) => { addLines(ls); toast('Activator added'); }} />
              )}
              {adder === 'paint' && <PaintAdder onAdd={(ls) => { addLines(ls); toast(ls.length > 1 ? 'Tricoat set added' : 'Paint added'); }} onClose={() => setAdder(null)} />}
              {adder === 'product' && <ProductAdder onAdd={addLines} onClose={() => setAdder(null)} />}
              {adder === 'custom' && <CustomAdder onAdd={addLines} onClose={() => setAdder(null)} />}
              {!adder && !editing && (
                <s-stack direction="inline" gap="base">
                  <s-button variant={q.lines.length ? 'secondary' : 'primary'} icon="color" onClick={() => setAdder('paint')}>Add paint</s-button>
                  <s-button icon="product" onClick={() => setAdder('product')}>Add products</s-button>
                  <s-button variant="tertiary" icon="plus" onClick={() => setAdder('custom')}>Custom item</s-button>
                </s-stack>
              )}
            </s-stack>
          </s-box>
        )}
      </s-section>

      {/* ── summary ── */}
      <s-section slot="aside" heading="Summary">
        <s-stack gap="base">
          <s-stack direction="inline" justifyContent="space-between">
            <s-text>Subtotal</s-text><span class="num">{money(totals.subtotal)}</span>
          </s-stack>

          {!locked ? (
            <s-grid gridTemplateColumns="96px 1fr" gap="small" alignItems="end">
              <s-select label="Discount" value={discount.type} onChange={(e) => edit({ discount: { ...discount, type: e.currentTarget.value } })}>
                <s-option value="FIXED_AMOUNT">$</s-option>
                <s-option value="PERCENTAGE">%</s-option>
              </s-select>
              <s-number-field
                label="Discount amount"
                labelAccessibilityVisibility="exclusive"
                min={0}
                step={discount.type === 'PERCENTAGE' ? 1 : 0.01}
                value={discount.value ? String(discount.value) : ''}
                placeholder="0"
                onChange={(e) => {
                  const v = parseFloat(e.currentTarget.value) || 0;
                  edit({ discount: v > 0 ? { ...discount, value: v, title: discount.title || 'Discount' } : null });
                }}
              />
            </s-grid>
          ) : totals.discount ? (
            <s-stack direction="inline" justifyContent="space-between"><s-text>Discount</s-text><span class="num">−{money(totals.discount)}</span></s-stack>
          ) : null}
          {!locked && totals.discount > 0 && <s-text color="subdued">−{money(totals.discount)}</s-text>}

          {!locked ? (
            <s-stack gap="small-200">
              <s-grid gridTemplateColumns={sk === 'none' || sk === 'pickup' ? '1fr' : '1fr 110px'} gap="small" alignItems="end">
                <s-select
                  label="Shipping"
                  value={sk}
                  onChange={(e) => {
                    const k = e.currentTarget.value;
                    edit({ shipping: SHIP_KINDS[k] ? { kind: k, title: SHIP_KINDS[k].title, price: k === 'std' ? Number(app.settings.shipStandard) : k === 'pickup' ? 0 : q.shipping?.price || 0, manual: false } : null });
                  }}
                >
                  <s-option value="std">Standard shipping</s-option>
                  <s-option value="pickup">Local pickup (free)</s-option>
                  <s-option value="delivery">Local delivery</s-option>
                  <s-option value="custom">Other shipping</s-option>
                  <s-option value="none">No shipping line</s-option>
                </s-select>
                {sk !== 'none' && sk !== 'pickup' && (
                  <s-money-field
                    label="Shipping price"
                    labelAccessibilityVisibility="exclusive"
                    value={q.shipping ? Number(q.shipping.price).toFixed(2) : ''}
                    onChange={(e) => edit({ shipping: { ...q.shipping, price: round2(parseFloat(e.currentTarget.value) || 0), manual: true } })}
                  />
                )}
              </s-grid>
              {sk === 'std' && (
                <s-stack direction="inline" gap="small" alignItems="center">
                  <s-text color="subdued">{q.shipping.manual ? 'Price set by hand.' : ruleNote}</s-text>
                  {q.shipping.manual && <s-button variant="tertiary" onClick={() => edit({ shipping: { ...q.shipping, manual: false } })}>Use store rule</s-button>}
                </s-stack>
              )}
            </s-stack>
          ) : q.shipping ? (
            <s-stack direction="inline" justifyContent="space-between"><s-text>{q.shipping.title}</s-text><span class="num">{money(totals.shipping)}</span></s-stack>
          ) : null}

          <s-stack direction="inline" justifyContent="space-between">
            <s-text>{(totals.taxesIncluded ?? app.shop.taxesIncluded) ? 'Tax (included in prices)' : 'Tax'}</s-text>
            <span class="num">{totals.tax == null ? (q.lines.length ? (calcErr ? '—' : 'calculating…') : money(0)) : money(totals.tax)}</span>
          </s-stack>
          {calcErr && <s-text tone="critical">{calcErr}</s-text>}
          <s-divider />
          <s-stack direction="inline" justifyContent="space-between">
            <s-text type="strong">Total</s-text>
            <s-text type="strong"><span class="num">{money(totals.total)}</span></s-text>
          </s-stack>
          {(totals.taxesIncluded ?? app.shop.taxesIncluded) && app.shop.taxesIncluded && (
            <s-text color="subdued">Your store's prices include sales tax (Shopify ▸ Settings ▸ Taxes), so the total does not add it on top.</s-text>
          )}
          {!dirty && !locked && q.id && totals.taxesIncluded && !app.shop.taxesIncluded && (
            <s-stack gap="small-200">
              <s-text color="subdued">This quote was priced while your store's prices included tax. Re-price it to add tax on top.</s-text>
              <s-stack direction="inline"><s-button loading={saving} onClick={save}>Re-price with tax</s-button></s-stack>
            </s-stack>
          )}
          {!q.customer && q.lines.length > 0 && <s-text color="subdued">Tax is estimated until a customer with an address is picked.</s-text>}
        </s-stack>
      </s-section>

      {q.group && (
        <s-section slot="aside" heading="Options">
          <s-stack gap="base">
            <s-text-field label="Name of this option" placeholder="e.g. Half gallon, Full gallon kit" value={q.option} disabled={locked}
              onInput={(e) => edit({ option: e.currentTarget.value })} details="Shown to the customer above this option's table." />
            <s-stack gap="small-200">
              {options.map((o) => (
                <s-grid key={o.id} gridTemplateColumns="1fr auto" gap="small-200" alignItems="center">
                  <s-clickable onClick={() => o.id !== q.id && route(`/quotes/${idOf(o.id)}`)} padding="small-200" borderRadius="base"
                    background={o.id === q.id ? 'subdued' : undefined}>
                    <s-grid gridTemplateColumns="1fr auto" gap="small" alignItems="center">
                      <s-stack gap="none">
                        <s-text type="strong">{o.option || o.name}</s-text>
                        <s-text color="subdued">{o.name}{o.id === q.id ? ' · this one' : ''}</s-text>
                      </s-stack>
                      <s-text>{money(o.total)}</s-text>
                    </s-grid>
                  </s-clickable>
                  {!locked && o.status !== 'COMPLETED' && (
                    <s-button variant="tertiary" icon="delete" accessibilityLabel={`Remove ${o.option || o.name}`} onClick={() => setRemoving(o)} />
                  )}
                </s-grid>
              ))}
              {removing && (
                <s-box padding="base" borderRadius="base" border="base" background="subdued">
                  <s-stack gap="small">
                    <s-text type="strong">Remove {removing.option || removing.name}?</s-text>
                    <s-text color="subdued">Its quote ({removing.name}) is deleted and its pay link stops working. The other options stay.</s-text>
                    <s-stack direction="inline" gap="small">
                      <s-button variant="primary" tone="critical" loading={removeBusy} onClick={confirmRemove}>Remove</s-button>
                      <s-button onClick={() => setRemoving(null)}>Cancel</s-button>
                    </s-stack>
                  </s-stack>
                </s-box>
              )}
            </s-stack>
            {!locked && <s-button icon="plus" loading={adding} onClick={newOption}>Add another option</s-button>}
          </s-stack>
        </s-section>
      )}

      <s-section slot="aside" heading="Status">
        <s-stack gap="small">
          <s-stack direction="inline" gap="small" alignItems="center">
            <s-badge tone={dirty ? 'attention' : st.tone}>{dirty ? 'Unsaved changes' : isNew ? 'New' : st.label}</s-badge>
          </s-stack>
          {q.sentAt && <s-text color="subdued">Emailed {daysAgo(q.sentAt)}{q.sent?.to ? ` to ${q.sent.to}` : ''}{q.sent?.via === 'shopify' ? ' (Shopify invoice)' : ''}</s-text>}
          {q.createdAt && <s-text color="subdued">Created {daysAgo(q.createdAt)}</s-text>}
          {q.invoiceUrl && !locked && (
            <s-text-field label="Pay link" readOnly value={q.invoiceUrl} details="The customer's checkout for this quote." />
          )}
        </s-stack>
      </s-section>

      <s-section slot="aside" heading="Notes">
        <s-stack gap="base">
          <s-text-field label="PO number" value={q.poNumber} disabled={locked} onInput={(e) => edit({ poNumber: e.currentTarget.value })} />
          <s-text-area label="Internal note" details="Only staff see this. It carries over to the order." rows={3} value={q.note} disabled={locked} onInput={(e) => edit({ note: e.currentTarget.value })} />
        </s-stack>
      </s-section>
    </s-page>
  );
}
